import { type ReactNode, useState } from "react";
import { Collapsible } from "@base-ui/react/collapsible";
import { IconChevronDown } from "@tabler/icons-react";
import styles from "./collapsibleStyles.module.css";
// @ts-expect-error pure js
import { classnames } from "../../utils";
import Text, { type Weight } from "./Text";

export type CollapsibleVariant = "card" | "plain";

interface CollapsibleCardProps {
  title: ReactNode;
  headerActions?: ReactNode;
  children?: ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  /** "card" (default) is the bordered blue-header look; "plain" is lighter. */
  variant?: CollapsibleVariant;
  titleWeight?: Weight | "regular";
  className?: string;
}

export default function CollapsibleCard({
  title,
  headerActions,
  children,
  defaultOpen,
  open,
  onOpenChange,
  disabled,
  variant = "card",
  titleWeight = "bold",
  className,
}: CollapsibleCardProps) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false);
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;
  const isPlain = variant === "plain";
  const hasContent = Boolean(children);

  function handleChange(next: boolean) {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  }

  const titleNode = (
    <Text
      as="div"
      weight={titleWeight === "regular" ? undefined : titleWeight}
      className={classnames(styles.title, isPlain && styles.titleGrow)}
    >
      {title}
    </Text>
  );

  return (
    <Collapsible.Root
      open={hasContent && isOpen}
      onOpenChange={handleChange}
      disabled={disabled}
      className={classnames(
        isPlain ? styles.itemPlain : styles.itemCard,
        className,
      )}
    >
      <div className={classnames(styles.header, !isPlain && styles.headerCard)}>
        {hasContent ? (
          <Collapsible.Trigger
            className={classnames(
              styles.trigger,
              isPlain && styles.triggerPlain,
            )}
            nativeButton={false}
            render={
              <div>
                <IconChevronDown
                  size={18}
                  className={classnames(
                    styles.chevron,
                    isPlain && styles.chevronEnd,
                  )}
                />
                {titleNode}
              </div>
            }
          />
        ) : (
          <div
            className={classnames(
              styles.triggerStatic,
              isPlain && styles.triggerStaticPlain,
            )}
          >
            {titleNode}
          </div>
        )}
        {headerActions && <div className={styles.actions}>{headerActions}</div>}
      </div>
      {hasContent && (
        <Collapsible.Panel
          className={classnames(
            styles.panel,
            isPlain ? styles.panelPlain : styles.panelCard,
          )}
        >
          <div>{children}</div>
        </Collapsible.Panel>
      )}
    </Collapsible.Root>
  );
}
